import React, { FC, useMemo } from 'react';
import { SolutionComboboxProps } from './SolutionComboboxProps';
import InputField from '@ingka/input-field';
import ListBox, { ListBoxGroup, MenuItem } from '@ingka/list-box';
import { REAL_ESTATE_SOLUTIONS } from '../SolutionModal/utils';
import useTranslation from '../../hooks/useTranslation';
import { Stack } from '@mui/material';
import { filterByLabels, returnListOfOptions } from './utils';
import HelperText from '@ingka/helper-text';
import Down from '@ingka/ssr-icon/paths/chevron-down-small.js';
import Up from '@ingka/ssr-icon/paths/chevron-up-small.js';
const SolutionCombobox: FC<SolutionComboboxProps> = ({
  value,
  disabled,
  onChange,
}) => {
  const t = useTranslation();
  function customSelectionTitle(selectionIds) {
    if (selectionIds.length === 0) return '';
    const [category, type] = selectionIds[0].split('-');
    return `${t(`solutionCategories.${category}`)} - ${t(`solutionTypes.${type}`)}`;
  }

  const [filterValue, setFilterValue] = React.useState(
    customSelectionTitle(value),
  );
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [focused, setFocused] = React.useState(false);

  const focusTrigger = () => {
    setFocused(true);
  };

  const optionClicked = (category: string, type: string) => {
    onChange(category, type);
    setFilterValue(
      `${t(`solutionCategories.${category}`)} - ${t(`solutionTypes.${type}`)}`,
    );
    setFocused(false);
  };
  const solutionOptions = useMemo(() => {
    return returnListOfOptions(t);
  }, [t]);
  const filteredOptions = useMemo(
    () => filterByLabels(solutionOptions, filterValue),
    [filterValue],
  );
  return (
    <Stack
      sx={{
        '.list-box__trigger .list-box': {
          width: '100%',
          maxWidth: 'none',
          marginBlock: 0,
        },
      }}
    >
      <ListBox
        id={'custom-combobox-list'}
        popupMode
        open={focused}
        domFocus={false}
        value={value}
        trigger={
          <InputField
            id={'custom-combobox'}
            label="Solution category and solution type"
            ref={inputRef}
            readOnly={disabled}
            iconPosition={'trailing'}
            iconOnClick={() => {
              if (!focused) {
                inputRef.current?.focus();
                return;
              }
            }}
            type="text"
            ssrIcon={focused ? Up : Down}
            onBlur={(e) => {
              if (
                e.relatedTarget &&
                e.relatedTarget.className !== 'menu-item'
              ) {
                setFocused(false);
              }
            }}
            value={focused ? filterValue : customSelectionTitle(value)}
            onChange={(e) => {
              setFilterValue(e.target.value);
            }}
            onFocus={focusTrigger}
          />
        }
      >
        {Object.keys(filteredOptions).map((category) => {
          return (
            <ListBoxGroup
              id={`group-${category}`}
              groupName={t(`solutionCategories.${category}`)}
            >
              {filteredOptions[category]?.items?.map((type) => {
                return (
                  <MenuItem
                    title={type.label}
                    id={`${category}-${type?.value}`}
                    disabled={REAL_ESTATE_SOLUTIONS.includes(type.value)}
                    onClick={(e) => {
                      e.stopPropagation();
                      optionClicked(category, type.value);
                    }}
                  />
                );
              })}
            </ListBoxGroup>
          );
        })}
      </ListBox>
      <HelperText id="sol-ty-category-helper-id" shouldValidate={false}>
        You can search for solution type and solution category
      </HelperText>
    </Stack>
  );
};

export default SolutionCombobox;
